import { AitHeaderCellData, AitHeaderGroupData, AitLocationHeaderCell } from "./interface";
import { trGetCellFromLocation } from "./trGetCellFromLocation";

export interface trCanAddColSpanReturn<T> {
  canAdd: true;
  reasonNot: undefined;
  targetCell: AitHeaderCellData<T>;
  hideCell: AitHeaderCellData<T>;
}

export interface trCanNotAddColSpanReturn<T> {
  canAdd: false;
  reasonNot: string;
  targetCell: AitHeaderCellData<T> | undefined;
  hideCell: AitHeaderCellData<T> | undefined;
}

/**
 * Determine whether a column span can be added at the specified header cell location.
 *
 * @param headerData - Header group data to inspect
 * @param location - Target header cell location
 * @param newColSpan - Desired new column span value
 * @param rowHeaderColumns - Number of row header columns (used to prevent spanning into row headers)
 * @returns An object describing whether the operation can proceed (`canAdd: true`) and,
 *          when allowed, the `targetCell` to expand and `hideCell` to be hidden. If not allowed,
 *          `canAdd` is false and `reasonNot` explains why.
 */
export const trCanAddColSpan = <T>(
  headerData: AitHeaderGroupData<T>,
  location: AitLocationHeaderCell,
  newColSpan: number,
  rowHeaderColumns: number,
): trCanAddColSpanReturn<T> | trCanNotAddColSpanReturn<T> => {
  let reasonNot = "";
  let hideCell: AitHeaderCellData<T> | undefined;

  // Check target cell exists
  const targetCell = trGetCellFromLocation(
    {
      headerData: headerData,
      bodyData: [],
      coreTrafficLight: "BlankNotEvaluated",
      columnInformation: [],
    },
    location,
    true,
  ) as AitHeaderCellData<T> | undefined;
  if (targetCell === undefined) {
    reasonNot = "AddColSpan: target cell not found";
  } else if (location.isRepeat) {
    reasonNot = "AddColSpan: this is a repeat cell";
    // Get the cell to increase row span
  } else if (targetCell.rowSpan !== 1) {
    reasonNot = "AddColSpan: cell already has row span";
  } else if (location.originalColumn + newColSpan === rowHeaderColumns + 1) {
    reasonNot = "AddColSpan: colSpan would go over the rowHeader";
  } else if (location.row + 1 === headerData.rows.length) {
    reasonNot = "AddColSpan: cannot add colSpan on the last row";
  } else {
    // Get the cell to hide
    const hideColumn = location.originalColumn + newColSpan - 1;
    if (headerData.rows[location.row].cells.length <= hideColumn) {
      reasonNot = "AddColSpan: header does not have enough columns to expand";
    } else {
      hideCell = headerData.rows[location.row].cells[hideColumn];
      if (hideCell.colSpan !== 1 || hideCell.rowSpan !== 1) {
        reasonNot = "AddColSpan: hide cell is not suitable for hiding";
      }
    }
  }

  return reasonNot !== ""
    ? { canAdd: false, reasonNot, targetCell, hideCell }
    : {
        canAdd: true,
        reasonNot: undefined,
        targetCell: targetCell!,
        hideCell: hideCell!,
      };
};

/**
 * Apply a column span change to header data. This will modify the target cell's
 * `colSpan` and set the corresponding hide cell's `colSpan` to 0.
 *
 * @param headerData - Header group data to modify
 * @param location - Location describing which header cell to update
 * @param newSpan - New colSpan value to apply to the target cell
 * @param rowHeaderColumns - Number of row header columns (used for validation)
 * @returns Updated `AitHeaderGroupData` with the applied column span changes
 * @throws Error when the change is not permitted (reason provided by `trCanAddColSpan`)
 */
export const trAddColSpan = <T>(
  headerData: AitHeaderGroupData<T>,
  location: AitLocationHeaderCell,
  newSpan: number,
  rowHeaderColumns: number,
) => {
  // Check ok to add row span
  const { canAdd, reasonNot, targetCell, hideCell } = trCanAddColSpan(
    headerData,
    location,
    newSpan,
    rowHeaderColumns,
  );

  // Already done
  if (targetCell?.colSpan === newSpan) return;

  // Cannot add
  if (!canAdd) throw new Error(reasonNot);

  // Create return object
  const newHeaderData = { ...headerData };
  newHeaderData.rows = headerData.rows.map((row) => {
    if (row.aitid !== location.rowId) return row;
    const newCells = row.cells.map((cell) => {
      if (cell.aitid === targetCell.aitid)
        return {
          ...cell,
          colSpan: newSpan,
        };
      if (cell.aitid === hideCell.aitid) {
        return {
          ...cell,
          colSpan: 0,
        };
      }
      return cell;
    });
    return { ...row, cells: newCells };
  });
  return newHeaderData;
};
